import { memoize } from 'lodash'
import { partytownSnippet } from 'partytown-wix-fork/integration'
import { applyHook, getHook, setHook } from './partyTownHooks'

const PARTYTOWN_ARTIFACT_PATH = 'wix-thunderbolt/dist/partytown/'

const LOCAL_PARTYTOWN_LIB = '/~partytown/'
const PROD_PARTYTOWN_LIB = `/_partials/${PARTYTOWN_ARTIFACT_PATH}`

const LOCALHOST_PARTYTOWN_LIB = 'http://localhost:4001/~partytown/'
const PROD_PARASTORAGE_PARTYTOWN_LIB = `https://static.parastorage.com/services/${PARTYTOWN_ARTIFACT_PATH}`

interface PartyTownHeadScriptParams {
	debug: boolean
	local: boolean
	domIsolation: boolean
	originIsolation: boolean
	credentialless: boolean
}

export const getPartytownConfig = (debug: boolean) => {
	const params = debug
		? {
				debug: true,
				logCalls: true,
				logGetters: true,
				logImageRequests: true,
				logMainAccess: true,
				logScriptExecution: true,
				logSendBeaconRequests: true,
				logSetters: true,
		  }
		: {}

	const lib = {
		forward: ['externalComponentRendererAPI.registerRemoteUiPort'],
		lib: PROD_PARTYTOWN_LIB,
		disableDimensionCache: true,
	}

	return {
		...params,
		...lib,
	}
}

const getLib = ({ local, originIsolation }: PartyTownHeadScriptParams) => {
	if (originIsolation) {
		return local ? LOCALHOST_PARTYTOWN_LIB : PROD_PARASTORAGE_PARTYTOWN_LIB
	}

	return local ? LOCAL_PARTYTOWN_LIB : PROD_PARTYTOWN_LIB
}
const getSandboxLib = ({ local, originIsolation }: PartyTownHeadScriptParams) => {
	if (originIsolation) {
		return local ? LOCALHOST_PARTYTOWN_LIB : PROD_PARASTORAGE_PARTYTOWN_LIB
	}

	return undefined
}

export const getPartytownHeadScript = memoize(
	(partyTownHeadScriptParams: PartyTownHeadScriptParams) => {
		const { debug, domIsolation, credentialless } = partyTownHeadScriptParams
		const params = getPartytownConfig(debug)

		const snippet = partytownSnippet({
			...params,
			lib: getLib(partyTownHeadScriptParams),
			credentialless,
			sandboxLib: getSandboxLib(partyTownHeadScriptParams),
			domIsolation,
			domIsolationSelectors: '[data-byoc-root]',
			mainWindowAccessors: [
				'createRemotePortal',
				'_getRealNode',
				'_getFakeNode',
				'_addEventHandler',
				'_addEventListener',
				'_removeEventListener',
			],
			get: domIsolation ? getHook : undefined,
			set: domIsolation ? setHook : undefined,
			apply: domIsolation ? applyHook : undefined,
			/* eslint-disable no-var */
			// We use var here for a reason.
			// This code is not being transpiled, it is taken as is into the main-head.ejs response
			// Therefor must support old browsers
			resolveUrl(url: URL, location: Location) {
				if (url.hostname === 'connect.facebook.net' || url.hostname === 'www.facebook.com') {
					var proxyUrl = new URL(location.origin + '/_serverless/partytown-proxy/resolve')
					proxyUrl.searchParams.append('url', url.href)
					return proxyUrl
				}
				return url
			},
		})

		const validateServiceWorker = process.env.DEBUG
			? 'if (!("serviceWorker" in navigator)) { console.error(`To enable service worker in local env, You should add your site origin here: chrome://flags/#unsafely-treat-insecure-origin-as-secure`) }'
			: ''

		/**
		 * api docs:
		 * TagLoaded: https://bo.wix.com/wix-docs/fe-guild/more-client-platforms/tag-manager-client/viewer-api#more-client-platforms_tag-manager-client_viewer-api_viewertagloaded
		 * ptupdate: https://partytown.builder.io/partytown-scripts#dynamically-appending-scripts
		 */
		return `
			${validateServiceWorker}
			${snippet}\n

			window.addEventListener("TagLoaded", function() {
				window.dispatchEvent(new CustomEvent('ptupdate'));
			});
		`
	},
	(params) => JSON.stringify(params)
)
